
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function SmartSecuritySystemsPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Smart Security Systems',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Smart security system installation in Fresno, CA. Smart cameras, locks, sensors, and integrated monitoring with mobile alerts and remote access.',
    url: `${siteUrl}/services/smart-home-energy/smart-security-systems`,
  };

  return (
    <>
      <SEOHead
        title="Smart Security Systems Fresno CA | Smart Cameras Locks Installation"
        description="Smart security system installation in Fresno, CA. Smart cameras, locks, motion sensors, and integrated monitoring. Mobile alerts, remote access, and professional installation."
        canonical="/services/smart-home-energy/smart-security-systems"
        keywords="smart security Fresno, smart cameras installation, smart locks, home security automation"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Smart Security Systems — Fresno, CA"
        heroSubtitle="Protect your Fresno home with intelligent security technology"
        heroImage="https://public.readdy.ai/ai/img_res/b82f0487579ffc92f4f9339ca387737c.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Smart%20security%20control%20panel%20with%20touchscreen%20display%20showing%20camera%20feeds%20and%20sensor%20status%20with%20smartphone%20app%20interface%20visible%20in%20contemporary%20home%20interior%20no%20people%20sleek%20modern%20design&width=700&height=600&seq=smartsec-fresno-01&orientation=portrait"
        fresnoContent="Fresno homeowners are upgrading to smart security systems that provide real-time monitoring, instant alerts, and remote access from anywhere. Our integrated security installations combine smart cameras, locks, motion sensors, and doorbell cameras into one unified system you control from your smartphone. Perfect for Fresno neighborhoods including Woodward Park, Fig Garden, Tower District, and surrounding areas, our professional installations integrate with your existing smart home system and provide peace of mind whether you're home or away."
        features={[
          {
            icon: 'ri-camera-line',
            title: 'Smart Camera Installation',
            description: 'Indoor and outdoor cameras with mobile viewing',
          },
          {
            icon: 'ri-lock-line',
            title: 'Smart Lock Setup',
            description: 'Keyless entry with remote access',
          },
          {
            icon: 'ri-sensor-line',
            title: 'Motion Sensor Integration',
            description: 'Automated alerts and lighting triggers',
          },
          {
            icon: 'ri-door-open-line',
            title: 'Smart Doorbell Installation',
            description: 'Video doorbell with two-way audio',
          },
          {
            icon: 'ri-notification-line',
            title: 'Mobile Alerts',
            description: 'Instant notifications for all activity',
          },
          {
            icon: 'ri-home-gear-line',
            title: 'System Integration',
            description: 'Unified control with lighting and climate',
          },
        ]}
        whatsIncluded={[
          'Complete security assessment and system design',
          'Professional installation of smart cameras (indoor/outdoor)',
          'Smart lock installation with keyless entry setup',
          'Motion sensor and door/window sensor installation',
          'Smart doorbell installation with video recording',
          'Wi-Fi network optimization for security devices',
          'Mobile app setup with user accounts',
          'Cloud storage configuration for video recordings',
          'Integration with existing smart home systems',
          'Complete system testing and user training',
        ]}
        faqs={[
          {
            question: 'What smart security features are most important for Fresno homes?',
            answer:
              "For Fresno homes, we recommend outdoor cameras with heat-resistant housings (our summers are brutal), smart locks for keyless entry, motion-activated lighting that deters intruders, and door/window sensors for all entry points. Video doorbells are essential for package theft prevention. Many Fresno homeowners also add smart garage door controllers since garages are common entry points. We design systems specifically for Fresno's climate and security concerns.",
          },
          {
            question: 'How much does smart security system installation cost in Fresno?',
            answer:
              "Basic smart security (2-3 cameras, smart doorbell, and smart lock) for a typical Fresno home costs $1,500-$2,500 installed. Comprehensive systems with 6-8 cameras, multiple smart locks, motion sensors, and integrated monitoring range from $3,500-$6,000. There are no monthly monitoring fees with most DIY systems, though cloud storage for video recordings typically costs $3-$10/month per camera. The investment significantly increases your Fresno home's security and value.",
          },
          {
            question: 'Do smart security cameras work during Fresno power outages?',
            answer:
              "Most smart cameras require power, so they won't record during outages unless you have battery backup. We recommend battery-powered cameras for critical locations or integrating your security system with a UPS (uninterruptible power supply) or whole-home battery backup. For Fresno homes concerned about summer power outages, we can design systems that maintain security monitoring even when the grid is down, with cameras that continue recording to local storage.",
          },
          {
            question: 'Can smart security integrate with my existing alarm system in Fresno?',
            answer:
              'Often yes! Many traditional alarm systems can be upgraded with smart components like cameras and smart locks. We can integrate new smart devices with your existing alarm panel, or we can replace your old system entirely with a modern smart security system. For Fresno homes with monitored alarm systems, we ensure new smart devices work alongside your existing monitoring service, providing both professional monitoring and DIY smartphone control.',
          },
          {
            question: 'How is video stored from my smart security cameras in Fresno?',
            answer:
              'Smart cameras typically offer cloud storage (recordings stored on company servers) or local storage (recordings stored on a hub or SD card in your Fresno home). Cloud storage provides access from anywhere and protects recordings if equipment is stolen, but requires monthly fees. Local storage is free but vulnerable to theft. We help you choose the best option based on your security needs and budget, and we can set up hybrid systems that use both.',
          },
          {
            question: 'How long does smart security installation take in a Fresno home?',
            answer:
              'Basic smart security installation (2-3 cameras, doorbell, and smart lock) takes 4-6 hours. Comprehensive systems with 6-8 cameras, multiple locks, motion sensors, and full integration take 1-2 days for a typical Fresno home. We handle all wiring, mounting, network setup, and app configuration. Outdoor camera installation may require running wires through attics or walls, which we do cleanly and professionally. We test everything thoroughly before we leave.',
          },
        ]}
        ctaText="Secure Your Home Today"
      />
    </>
  );
}
